/**
 * Specialty Registry — the runtime API.
 *
 * rev v11.0.0 · Phase 2 / C1
 *
 * The registry is the only place the platform is allowed to answer the
 * question "what does this clinical workspace look like?". It is pure data
 * plus pure functions: no React, no router, no HTTP client. That is what
 * makes it testable in isolation and reusable from the API, the web app and
 * the CI gates alike.
 */
import { SPECIALTIES, SPECIALTY_SLUGS } from "./specialties";
import type {
  NavModule,
  PanelSchemaType,
  SpecialtyDefinition,
  ThemeTokens,
} from "./types";
import { isKnownSpecialty, resolveSpecialtyId } from "./slug";

/** Capabilities a tenant/physician can hold, used to filter navigation. */
export type CapabilitySet = ReadonlySet<string> | readonly string[];

function hasCapability(caps: CapabilitySet, capability: string): boolean {
  if (capability === "") return true;
  if (Array.isArray(caps)) return caps.includes(capability);
  return (caps as ReadonlySet<string>).has(capability);
}

/**
 * The registry.
 *
 * A class rather than a bag of functions so that the dependency graph stays
 * explicit: the registry is constructed once and injected where needed.
 */
export class SpecialtyRegistry {
  private readonly bySlug: Map<string, SpecialtyDefinition>;

  constructor(specialties: readonly SpecialtyDefinition[] = SPECIALTIES) {
    const map = new Map<string, SpecialtyDefinition>();
    for (const specialty of specialties) {
      if (map.has(specialty.id)) {
        throw new Error(`duplicate specialty id '${specialty.id}'`);
      }
      map.set(specialty.id, specialty);
    }
    this.bySlug = map;
  }

  /** Every specialty, in clinical order. */
  list(): readonly SpecialtyDefinition[] {
    return SPECIALTIES;
  }

  /** Canonical slugs, in clinical order. */
  slugs(): readonly string[] {
    return SPECIALTY_SLUGS;
  }

  /**
   * Get a specialty by slug OR by legacy code. Returns null when unknown.
   *
   * Accepting both vocabularies at this boundary is deliberate: callers
   * receive identifiers from three different worlds (the URL, the NGAP
   * catalogue and the legacy nomenclature) and none of them should have to
   * know which one they hold.
   */
  get(id: string | null | undefined): SpecialtyDefinition | null {
    const slug = resolveSpecialtyId(id);
    if (!slug) return null;
    return this.bySlug.get(slug) ?? null;
  }

  /** Get a specialty or throw. Use where the id is known to be valid. */
  require(id: string): SpecialtyDefinition {
    const specialty = this.get(id);
    if (!specialty) {
      throw new Error(`unknown specialty '${id}'`);
    }
    return specialty;
  }

  /** True when the identifier resolves. */
  has(id: string | null | undefined): boolean {
    return isKnownSpecialty(id);
  }

  /**
   * Navigation for a specialty, filtered by the caller's capabilities.
   *
   * A module whose `requires` list is not satisfied is removed entirely
   * rather than disabled: a hidden ECG module cannot be reached by keyboard,
   * cannot be deep-linked by mistake, and cannot be audited.
   */
  navigation(
    id: string | null | undefined,
    capabilities: CapabilitySet = [],
  ): NavModule[] {
    const specialty = this.get(id);
    if (!specialty) return [];
    return specialty.navigation
      .filter((m) => m.requires.every((cap) => hasCapability(capabilities, cap)))
      .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  }

  /** Clinical panels for a specialty, filtered by capabilities. */
  panels(
    id: string | null | undefined,
    capabilities: CapabilitySet = [],
  ): PanelSchemaType[] {
    const specialty = this.get(id);
    if (!specialty) return [];
    return specialty.panels
      .filter((p) => p.requires.every((cap) => hasCapability(capabilities, cap)))
      .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  }

  /**
   * The feature-flag key that gates this specialty's new experience.
   *
   * One flag per specialty — never a global "new UX" flag. The whole point
   * of the registry is that a rollout can be reversed for one specialty
   * without touching the others.
   */
  featureFlag(id: string | null | undefined): string | null {
    return this.get(id)?.featureFlag ?? null;
  }

  /** The theme tokens for a specialty. */
  theme(id: string | null | undefined): ThemeTokens | null {
    return this.get(id)?.theme ?? null;
  }

  /**
   * Inline style object that applies the specialty theme to a DOM subtree.
   *
   * The web layer writes this onto `[data-specialty="<slug>"]`. Because the
   * 48 shadcn/ui components read the same CSS variables, the whole
   * interface recolours without a single component being forked.
   */
  themeStyle(id: string | null | undefined): Record<string, string> {
    const theme = this.theme(id);
    if (!theme) return {};
    return {
      "--primary": theme.primary,
      "--primary-foreground": theme.primaryForeground,
      "--accent": theme.accent,
      "--accent-foreground": theme.accentForeground,
      "--destructive": theme.destructive,
      "--success": theme.success,
      "--warning": theme.warning,
      "--surface": theme.surface,
      "--radius": theme.radius,
      "--font-display": theme.fontDisplay,
    };
  }

  /** The CSS selector that scopes a specialty. */
  scopeSelector(id: string | null | undefined): string {
    const slug = resolveSpecialtyId(id);
    return slug ? `[data-specialty="${slug}"]` : "[data-specialty]";
  }

  /**
   * The specialties a physician can work in, from their declared
   * specialisations.
   *
   * A doctor whose `doctorSpecs` include "Gynécologie-Obstétrique" works in
   * the `gynecologie` workspace. Matching is done on the label, because the
   * doctor's record carries the human-readable specialisation.
   */
  forDoctorSpecs(specs: readonly string[]): SpecialtyDefinition[] {
    const wanted = new Set(specs.map((s) => s.trim().toLowerCase()));
    if (wanted.size === 0) return [];
    return SPECIALTIES.filter((specialty) =>
      specialty.doctorSpecs.some((spec) =>
        wanted.has(spec.trim().toLowerCase()),
      ),
    );
  }

  /** Every legacy code known to the registry, mapped to its canonical slug. */
  vocabulary(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const specialty of SPECIALTIES) {
      for (const alias of specialty.aliases) map[alias] = specialty.id;
    }
    return map;
  }
}

/** The shared registry instance. */
export const specialtyRegistry = new SpecialtyRegistry();

export default specialtyRegistry;
